import { type ReactNode, useMemo, useState } from "react";
import { StyleSheet, View } from "react-native";

import {
  AppText,
  Badge,
  Button,
  ButtonText,
  Card,
  EmptyState,
  List,
  ListRow,
  MetricCard,
  ProtectedScreen,
  SearchableSelect,
  SectionHeader,
  Stack,
  TextField,
} from "~/components/ui";
import { spacing, useThemedStyles, type ThemeColors } from "~/styles/theme";
import type { SelectOption } from "~/utils/regional-options";

type FieldConfig = {
  key: string;
  label: string;
  placeholder: string;
  accessibilityLabel?: string;
  keyboardType?: "default" | "email-address" | "numeric";
  multiline?: boolean;
  options?: SelectOption[];
  optionsForValues?: (values: Record<string, string>) => SelectOption[];
  clearOnChange?: string[];
  selectTitle?: string;
};

export type ResourceMetric = {
  label: string;
  value: string;
};

export type ResourceListItem = {
  id: string;
  title: string;
  eyebrow?: string;
  description?: string;
  editValues?: Record<string, string>;
  meta?: string[];
  actions?: ReactNode;
};

export function ResourceListScreen({
  title,
  subtitle,
  primaryAction,
  emptyTitle,
  emptyDescription,
  fields,
  requiredField,
  metrics,
  items,
  loading,
  error,
  creating,
  updating = false,
  readOnly = false,
  toolbar,
  onCreate,
  onUpdate,
}: {
  title: string;
  subtitle: string;
  primaryAction: string;
  emptyTitle: string;
  emptyDescription: string;
  fields: FieldConfig[];
  requiredField: string;
  metrics: ResourceMetric[];
  items: ResourceListItem[];
  loading: boolean;
  error: unknown;
  creating: boolean;
  updating?: boolean;
  readOnly?: boolean;
  toolbar?: ReactNode;
  onCreate: (values: Record<string, string>) => Promise<unknown>;
  onUpdate?: (id: string, values: Record<string, string>) => Promise<unknown>;
}) {
  const stylesheet = useThemedStyles(makeStyles);
  const initialValues = useMemo(
    () =>
      fields.reduce<Record<string, string>>((values, field) => {
        values[field.key] = "";
        return values;
      }, {}),
    [fields],
  );
  const [values, setValues] = useState(initialValues);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const requiredValue = values[requiredField]?.trim() ?? "";
  const editRequiredValue = editValues[requiredField]?.trim() ?? "";
  const errorMessage = error instanceof Error ? error.message : "";

  async function submit() {
    if (!requiredValue || creating) return;
    await onCreate(values);
    setValues(initialValues);
  }

  function startEdit(item: ResourceListItem) {
    setEditingId(item.id);
    setEditValues({ ...initialValues, ...(item.editValues ?? {}) });
  }

  async function submitEdit(item: ResourceListItem) {
    if (!onUpdate || !editRequiredValue || updating) return;
    await onUpdate(item.id, editValues);
    setEditingId(null);
    setEditValues({});
  }

  function optionsForField(
    field: FieldConfig,
    currentValues: Record<string, string>,
  ) {
    return field.optionsForValues?.(currentValues) ?? field.options ?? [];
  }

  function withFieldChange(
    current: Record<string, string>,
    field: FieldConfig,
    value: string,
  ) {
    const next = { ...current, [field.key]: value };
    for (const key of field.clearOnChange ?? []) {
      next[key] = "";
    }
    return next;
  }

  return (
    <ProtectedScreen title={title} subtitle={subtitle}>
      {toolbar}
      {metrics.length > 0 ? (
        <View style={stylesheet.metricsGrid}>
          {metrics.map((metric) => (
            <View key={metric.label} style={stylesheet.metric}>
              <MetricCard label={metric.label} value={metric.value} />
            </View>
          ))}
        </View>
      ) : null}

      {!readOnly ? (
        <Card gap="md">
          <SectionHeader
            title={primaryAction}
            description="Capture the core details now and refine status, billing, and delivery as the work moves."
          />
          <View style={stylesheet.formGrid}>
            {fields.map((field) => (
              <View
                key={field.key}
                style={[
                  stylesheet.field,
                  field.multiline ? stylesheet.fullWidth : null,
                ]}
              >
                <AppText size="xs" weight="semibold">
                  {field.label}
                </AppText>
                {field.options || field.optionsForValues ? (
                  <SearchableSelect
                    title={field.selectTitle ?? field.label}
                    accessibilityLabel={field.label}
                    value={values[field.key] ?? ""}
                    options={optionsForField(field, values)}
                    placeholder={field.placeholder}
                    onValueChange={(value) =>
                      setValues((current) =>
                        withFieldChange(current, field, value),
                      )
                    }
                  />
                ) : (
                  <TextField
                    accessibilityLabel={field.accessibilityLabel ?? field.label}
                    value={values[field.key] ?? ""}
                    placeholder={field.placeholder}
                    keyboardType={field.keyboardType ?? "default"}
                    multiline={field.multiline}
                    style={field.multiline ? stylesheet.textarea : null}
                    onChangeText={(value) =>
                      setValues((current) => ({
                        ...current,
                        [field.key]: value,
                      }))
                    }
                  />
                )}
              </View>
            ))}
          </View>
          {errorMessage ? (
            <AppText size="xs" tone="danger">
              {errorMessage}
            </AppText>
          ) : null}
          <Button
            align="start"
            iconName="plus"
            disabled={!requiredValue || creating}
            onPress={() => void submit()}
          >
            <ButtonText>{creating ? "Saving..." : primaryAction}</ButtonText>
          </Button>
        </Card>
      ) : null}

      <Stack gap="sm">
        <View style={stylesheet.listHeader}>
          <AppText weight="semibold">{title}</AppText>
          <AppText size="xs" tone="muted">
            {loading ? "Loading…" : `${items.length}`}
          </AppText>
        </View>
        {items.length === 0 ? (
          <EmptyState
            title={emptyTitle}
            description={emptyDescription}
            icon="inbox"
          />
        ) : (
          <List>
            {items.map((item) => {
              if (editingId === item.id) {
                return (
                  <View key={item.id} style={stylesheet.editPanel}>
                    <View style={stylesheet.formGrid}>
                      {fields.map((field) => (
                        <View
                          key={field.key}
                          style={[
                            stylesheet.field,
                            field.multiline ? stylesheet.fullWidth : null,
                          ]}
                        >
                          <AppText size="xs" weight="semibold">
                            {field.label}
                          </AppText>
                          {field.options || field.optionsForValues ? (
                            <SearchableSelect
                              title={field.selectTitle ?? field.label}
                              accessibilityLabel={field.label}
                              value={editValues[field.key] ?? ""}
                              options={optionsForField(field, editValues)}
                              placeholder={field.placeholder}
                              onValueChange={(value) =>
                                setEditValues((current) =>
                                  withFieldChange(current, field, value),
                                )
                              }
                            />
                          ) : (
                            <TextField
                              accessibilityLabel={
                                field.accessibilityLabel ?? field.label
                              }
                              value={editValues[field.key] ?? ""}
                              placeholder={field.placeholder}
                              keyboardType={field.keyboardType ?? "default"}
                              multiline={field.multiline}
                              style={
                                field.multiline ? stylesheet.textarea : null
                              }
                              onChangeText={(value) =>
                                setEditValues((current) => ({
                                  ...current,
                                  [field.key]: value,
                                }))
                              }
                            />
                          )}
                        </View>
                      ))}
                    </View>
                    <View style={stylesheet.actionRow}>
                      <Button
                        compact
                        iconName="save"
                        disabled={!editRequiredValue || updating}
                        onPress={() => void submitEdit(item)}
                      >
                        <ButtonText>
                          {updating ? "Saving..." : "Save"}
                        </ButtonText>
                      </Button>
                      <Button
                        compact
                        variant="outline"
                        disabled={updating}
                        onPress={() => {
                          setEditingId(null);
                          setEditValues({});
                        }}
                      >
                        <ButtonText>Cancel</ButtonText>
                      </Button>
                    </View>
                  </View>
                );
              }

              const titleNode = (
                <Stack gap="none">
                  {item.eyebrow ? (
                    <AppText size="xs" tone="muted">
                      {item.eyebrow}
                    </AppText>
                  ) : null}
                  <AppText weight="semibold">{item.title}</AppText>
                </Stack>
              );
              const metaBadges =
                item.meta && item.meta.length > 0
                  ? item.meta.map((meta) => (
                      <Badge key={meta} tone="neutral">
                        {meta}
                      </Badge>
                    ))
                  : undefined;

              return (
                <ListRow
                  key={item.id}
                  title={titleNode}
                  description={item.description}
                  meta={metaBadges}
                  trailing={
                    item.actions ||
                    (!readOnly && onUpdate && item.editValues) ? (
                      <View style={stylesheet.actionRow}>
                        {!readOnly && onUpdate && item.editValues ? (
                          <Button
                            compact
                            iconName="pencil"
                            variant="outline"
                            disabled={updating}
                            onPress={() => startEdit(item)}
                          >
                            <ButtonText>Edit</ButtonText>
                          </Button>
                        ) : null}
                        {item.actions}
                      </View>
                    ) : undefined
                  }
                />
              );
            })}
          </List>
        )}
      </Stack>
    </ProtectedScreen>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    metricsGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.m,
    },
    metric: {
      minWidth: 150,
      flex: 1,
    },
    formGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.m,
    },
    field: {
      minWidth: 220,
      flex: 1,
      gap: spacing.xs,
    },
    fullWidth: {
      flexBasis: "100%",
    },
    textarea: {
      minHeight: 86,
      textAlignVertical: "top",
    },
    listHeader: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.m,
    },
    editPanel: {
      gap: spacing.m,
      padding: spacing.l,
      backgroundColor: colors.surfaceSunken,
    },
    actionRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.s,
    },
  });
